import React, { useContext, useRef, useState } from "react";
import { T } from "../../../hooks/useTranslate";
import { useAuth } from "../../../hooks/useAuth";

import { Stack, Typography } from "@mui/material";
import { RankQualAlertIcon } from "./RankQualAlertIcon";
import { LanguageContext } from "../../../hooks/LanguageContext";
import {
  isAsianMarket,
  formatActiveLegsDisplay,
  RANK_LEG_REQUIREMENTS,
  RANK_LEG_REQUIREMENTS_NON_ASIAN_MARKETS
} from "../../../constants/asianMarketRankQual.constants";
import { isAmericasMarket } from "../../../constants/markets";
import { DistributorContext } from "../../../hooks/DistributorContext";
import { PvBreakdownPopover } from "./PvBreakdownPopover";
import { ReactComponent as HelpIcon } from "../../../assets/icons/help.svg";


const RankQualPvTvBox = ({
  type,
  values,
  inDrawer,
  isError,
  selectedRankDetails,
  pvBreakdownStatus,
  pvBreakdownData,
}) => {
  const { localizeNumber } = useContext(LanguageContext);
  const { userInfo } = useAuth();
  const { distributorData } = useContext(DistributorContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const helpIconRef = useRef(null);

  // Asian market logic for active legs display (uses member being viewed's market)
  const memberCountryCode = inDrawer ? distributorData?.market : userInfo?.countryCode;
  const isAsianMarketUser = isAsianMarket(memberCountryCode);
  const rankCode = selectedRankDetails?.rank;
  const legRequirements = isAsianMarketUser ? RANK_LEG_REQUIREMENTS[rankCode] : RANK_LEG_REQUIREMENTS_NON_ASIAN_MARKETS[rankCode];

  // Americas market logic for TV checkmark (always uses logged-in user's market)
  const isAmericasUser = isAmericasMarket(userInfo?.countryCode);
  const isQualified = values?.qualified;
  const isPV = type === "pv";
  const hasPvBreakdown = isPV
    ? Array.isArray(pvBreakdownData?.data) && pvBreakdownData.data.length > 0
    : false;
  const hasData = isPV ? hasPvBreakdown : (values?.value ?? 0) > 0;

  /**
   * Get the correct qualification status based on type and market
   * 
   * TV Logic:
   * - Americas markets: Use raw TV value (excludes compressed volume) >= 1000
   * - Other markets: Use backend qualified field (includes compressed volume)
   * 
   * Based on GLOSSARY.md:
   * - values.value represents uncompressed TV (raw team volume)
   * - values.qualified uses backend logic that includes compressed TV
   */
  const getIsQualified = () => {
    // TV checkmark logic: Americas markets exclude compressed volume
    if (type === "tv" && isAmericasUser) {
      return (values?.value ?? 0) >= 1000;
    }

    // For active legs: use existing leg requirements logic
    if (type === "active_legs") {
      return !!(values?.value >= legRequirements);
    }

    // For all other cases: use API qualified status
    return isQualified;
  };

  const formatValueDisplay = () => {
    if (isError) {
      return "0";
    }

    const value = values?.value || 0;

    // Special formatting for active legs in Asian markets
    if (type === "active_legs" && isAsianMarketUser && rankCode) {
      return formatActiveLegsDisplay(value, rankCode);
    }

    return localizeNumber(value);
  };

  const handleClick = () => {
    if (isPV && !inDrawer && hasData) {
      setAnchorEl(helpIconRef.current);
    }
  };

  return (
    <>
    <Stack
      onClick={handleClick}
      data-testid="pv-tv-box"
      sx={{
        background: "rgba(145, 158, 171, 0.05)",
        flex: 1,
        borderRadius: "8px",
        ...(isPV && !inDrawer && hasData ? { cursor: 'pointer' } : {}),
      }}
    >
      <Stack id="1" direction="row" justifyContent={"space-between"}>
        <Stack pl={2} pt={2} pb={2} pr={2}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography
              sx={{
                opacity: ".6",
                fontSize: "var(--unicity-font-12px, 12px)"
              }}
            >
              <T>{type}</T>
            </Typography>
            {isPV && !inDrawer && hasData && (
              <span ref={helpIconRef} style={{ display: 'flex', alignItems: 'center' }}>
                <HelpIcon fill="currentColor" width={16} height={16} />
              </span>
            )}
          </Stack>
          <Stack direction="row" alignItems="center">
            <Typography
              data-test-id={`${type}-value`}
              variant="h9"
              sx={{
                fontWeight: "700",
                fontSize: "var(--unicity-font-16px, 16px)"
              }}>
              {formatValueDisplay()}&nbsp;
            </Typography>
            {
              type !== "total_ov" && type !== "active_legs" && (
                <Typography
                  data-test-id={`${type}-required-value`}
                  variant="h9"
                  sx={{
                    fontSize: "var(--unicity-font-16px, 16px)",
                    opacity: ".6"
                  }}
                  noWrap={true}
                >
                  / {isError ? 0 : localizeNumber(values?.requiredValue || 0)}
                </Typography>
              )
            }
            {
              type === "active_legs" && !isAsianMarketUser && (
                <Typography
                  data-test-id={`${type}-required-value`}
                  variant="h9"
                  sx={{
                    fontSize: "var(--unicity-font-16px, 16px)",
                    opacity: ".6"
                  }}
                  noWrap={true}
                >
                  / {isError ? 0 : localizeNumber(values?.requiredValue || 0)}
                </Typography>
              )
            }
          </Stack>
        </Stack>
        {!isError && type !== "total_ov" && <Stack p={1}>
          <RankQualAlertIcon
            type={type}
            values={values}
            isQualified={getIsQualified()}
          />
        </Stack>}
      </Stack>
    </Stack>
    {isPV && !inDrawer && (
      <PvBreakdownPopover
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        status={pvBreakdownStatus}
        data={pvBreakdownData}
      />
    )}
    </>
  )
}

export { RankQualPvTvBox }